import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Department } from 'src/app/shared/department';
import { ClientApiService } from 'src/app/services/client-api.service';

/**
 * Reusable department-picker dropdown, filtered by a parent company ID.
 *
 * Key collaborator: {@link ClientApiService} — `getDepartments(parentCompany)`.
 *
 * **Data-flow:** `parentCompany` change → `_reloadComponentData()` → list loaded →
 * `_selectDepartment()` resolves pre-selection from `selectedID`.
 *
 * **Inputs summary:**
 * | Field        | Purpose                                                              |
 * |--------------|----------------------------------------------------------------------|
 * | `parentCompany` | Company whose departments are loaded; list reloads on every change |
 * | `selectedID`    | Pre-selects the matching department after list loads               |
 * | `masterID`      | LCO/PCO company ID — reserved for future child-department resolver |
 * | `disabled`      | Passes through to the PrimeNG dropdown                            |
 * | `readonly`      | Prevents selection change without hiding the dropdown              |
 * | `showLabel`     | Toggles the label above the dropdown                              |
 * | `labelKey`      | Text content of the label                                          |
 * | `placeholder`   | Dropdown placeholder text                                          |
 *
 * **Output:** `selectedDepartmentID` — emits the selected department's `CLIID` on change,
 * or `null` when the dropdown is cleared.
 */
@Component({
  selector: 'app-departmentselector',
  templateUrl: './departmentselector.component.html',
  styleUrls: ['./departmentselector.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class DepartmentselectorComponent implements OnInit, OnChanges, OnDestroy {

  _departmentslistMC: Department[];
  _departmentMC: Department;
  _loading: boolean;

  totalRecordsMC: number;

  private readonly destroy$ = new Subject<void>();
  private readonly _reloadCancel$ = new Subject<void>();

  /** Toggles the label above the dropdown. */
  @Input() showLabel: boolean = true;

  /** Text content of the label. */
  @Input() labelKey = 'Отдел';

  /** Dropdown placeholder text. */
  @Input() placeholder = 'Выбрать отдел...';

  /** Company whose departments are loaded; list reloads on every change. */
  @Input() parentCompany: number = null;

  /** Pre-selects the matching department after the list loads. */
  @Input() selectedID: number = null;

  /** LCO/PCO company ID — reserved for future child-department resolver. */
  @Input() masterID: number;

  /** Passes through to the PrimeNG dropdown `disabled` property. */
  @Input() disabled: boolean = false;

  /** Prevents selection change without hiding the dropdown. */
  @Input() readonly: boolean = false;

  /** Emits the selected department's `CLIID` on change, or `null` on clear. */
  @Output() public selectedDepartmentID: EventEmitter<number> = new EventEmitter();

  constructor(
    private cliApi: ClientApiService,
  ) { }

  /**
   * Syncs `_departmentMC` from the loaded list using `selectedID`. Sets to `null`
   * when `selectedID` is not set or no matching entry is found.
   */
  private _selectDepartment(): void {
    this._departmentMC = (this.selectedID && this._departmentslistMC)
      ? this._departmentslistMC.find(x => x.CLIID === this.selectedID) ?? null
      : null;
  }

  /**
   * Guards on `parentCompany`; if absent exits early. Fetches the department list,
   * stores it, and calls `_selectDepartment()` to resolve any pre-selection.
   * Cancels any prior in-flight request before issuing a new one.
   * Logs errors without crashing the component.
   */
  private _reloadComponentData(): void {
    if (!this.parentCompany) { return; }
    this._reloadCancel$.next();
    this.cliApi.getDepartments(this.parentCompany).pipe(
      map(dep => {
        this._departmentslistMC = dep;
        this.totalRecordsMC = dep.length;
        this._selectDepartment();
      }),
      takeUntil(this._reloadCancel$),
      takeUntil(this.destroy$)
    ).subscribe({
      error: err => console.error('*** DepartmentselectorComponent _reloadComponentData error:', err)
    });
  }

  /**
   * Reacts to `@Input()` changes: `parentCompany` triggers a full list reload;
   * `selectedID` re-syncs the dropdown model without a server round-trip.
   */
  ngOnChanges(changes: SimpleChanges): void {
    for (const propName of Object.keys(changes)) {
      const change = changes[propName];

      switch (propName) {
        case 'parentCompany':
          this._reloadComponentData();
          break;

        case 'selectedID':
          this._selectDepartment();
          break;
      }
    }
  }

  /**
   * Completes `destroy$` to cancel all active subscriptions.
   */
  ngOnDestroy(): void {
    console.log('*** DepartmentselectorComponent in ngOnDestroy');
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Triggers the initial department list load.
   */
  ngOnInit(): void {
    this._reloadComponentData();
  }

  /**
   * Emits the selected department's `CLIID` via `selectedDepartmentID`, or emits
   * `null` when the dropdown value is cleared.
   */
  onChangeDS(_e: unknown): void {
    console.log('*** DepartmentselectorComponent onChangeDS: ' + JSON.stringify(this._departmentMC));
    if (this._departmentMC) {
      this.selectedID = this._departmentMC.CLIID;
      this.selectedDepartmentID.emit(this.selectedID);
    } else {
      this.selectedID = null;
      this.selectedDepartmentID.emit(null);
    }
  }
}
